import { useCallback, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import moment from "moment";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { gigaListRuns } from "./gigaApi";

const statusTone = {
	failed: { bg: "#FEF3F2", color: "#B42318", border: "#FECDCA" },
	campaigns: { bg: "#ECFDF3", color: "#067647", border: "#ABEFC6" },
	insights: { bg: "#EFF8FF", color: "#175CD3", border: "#B2DDFF" },
	explored: { bg: "#F4F3FF", color: "#5925DC", border: "#D9D6FE" },
	draft: { bg: grey[50], color: grey[700], border: grey[200] },
};

const StatusChip = ({ status }) => {
	const tone = statusTone[status] || statusTone.draft;
	return (
		<Chip
			size="small"
			label={status || "draft"}
			sx={{
				height: 22,
				fontWeight: 600,
				fontSize: 11.5,
				textTransform: "capitalize",
				bgcolor: tone.bg,
				color: tone.color,
				border: `1px solid ${tone.border}`,
				borderRadius: 1.25,
				"& .MuiChip-label": { px: 0.9 },
			}}
		/>
	);
};

const runColumns = [
	{
		key: "name",
		name: "Name",
		minWidth: 160,
		flex: 1.2,
		valueGetter: ({ row }) => row.name || "",
	},
	{
		key: "created_at",
		name: "Created",
		minWidth: 140,
		flex: 0.8,
		valueGetter: ({ row }) => (row.created_at ? moment(row.created_at).format("DD/MM/YY HH:mm") : ""),
	},
	{
		key: "seeds",
		name: "Seeds",
		minWidth: 180,
		flex: 1.5,
		valueGetter: ({ row }) => {
			const seeds = row.seeds || [];
			if (!seeds.length) return "";
			if (seeds.length <= 3) return seeds.join(", ");
			return `${seeds.slice(0, 2).join(", ")} +${seeds.length - 2}`;
		},
	},
	{ key: "country", name: "Country", minWidth: 100, flex: 0.6 },
	{
		key: "ads_account",
		name: "Ads account",
		minWidth: 130,
		flex: 0.8,
		valueGetter: ({ row }) => row.ads_account?.name || "",
	},
	{ key: "brand_name", name: "Brand", minWidth: 120, flex: 0.7 },
	{ key: "user_email", name: "User", minWidth: 150, flex: 1 },
	{
		key: "status",
		name: "Status",
		minWidth: 120,
		flex: 0.6,
		valueGetter: ({ row }) => row.status || "draft",
		renderCell: ({ value }) => <StatusChip status={value} />,
	},
	{
		key: "progress",
		name: "Stage",
		minWidth: 160,
		flex: 1,
		valueGetter: ({ row }) => row.progress?.at?.(-1)?.label || "",
	},
];

const GigaLogs = ({ onSelect }) => {
	const [runs, setRuns] = useState([]);

	const getServerSideData = useCallback(async (limit, offset) => {
		const result = await gigaListRuns({ limit, offset });
		return {
			rows: Array.isArray(result?.rows) ? result.rows : [],
			total_rows_count: Number(result?.total_rows_count) || 0,
		};
	}, []);

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				width: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				"& > .MuiPaper-root": {
					flex: 1,
					minHeight: 0,
					height: "100% !important",
					boxShadow: "none",
					borderRadius: 0,
					pl: 1.5,
					pr: 1.5,
					pt: 0.25,
					pb: 0.5,
				},
				"& tbody tr": { cursor: "pointer" },
			}}>
			<TanstackDatagrid
				columns={runColumns}
				pageData={runs}
				setPageData={setRuns}
				disablePagination
				serverSidePagination
				getServerSideData={getServerSideData}
				rowsPerPage={20}
				getRowId={(row) => row._id}
				handleRowClick={({ row }) => onSelect?.(row._id)}
				emptyMessage="No runs yet. Name the run, enter seeds, and click Run."
				rowHeight={40}
				headerHeight={36}
				headerRowStyle={{ background: "#FAFAFA" }}>
				<Box>
					<Typography fontWeight={700} fontSize={13.5} color={grey[800]} lineHeight={1.2}>
						Past runs
					</Typography>
					<Typography fontSize={11.5} color={grey[500]}>
						Click a row to open it
					</Typography>
				</Box>
			</TanstackDatagrid>
		</Box>
	);
};

export default GigaLogs;
